import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Dialog, Link as MuiLink } from '@mui/material';

import { Button } from '@/components/ui/button';
import { DataTablePagination } from '@/components/data-table-pagination-bar';
import { Status } from '@/models/campaign';
import { SUPPORT_EMAIL } from '@/utils/constants';
import ErrorBanner from '../../banners/ErrorBanner';
import EditPromotion from '../EditPromotion';
import PromotionListTable from '../../promotions/PromotionListTable';
import PromotionListTableHeader from '../../promotions/PromotionListTableHeader';
import { usePromotions, usePlacements } from './hooks';

export default function PromotionList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: placements } = usePlacements();
  const {
    data: promotions,
    refetch,
    status: promotionsStatus,
  } = usePromotions(
    {
      bannerIds: searchParams.getAll('banner'),
      page: searchParams.get('page'),
      perPage: searchParams.get('per_page'),
      search: searchParams.get('search'),
      sortBy: searchParams.get('sort_by'),
      sortDir: searchParams.get('sort_dir'),
      status: searchParams.getAll('status'),
      storeIds: searchParams.getAll('store'),
      zoneIds: searchParams.getAll('zone'),
    },
    placements ?? [],
  );
  const showEditPromotionScreen = location.pathname.includes('/promotions/');
  const selectedPromotionId: number | undefined =
    Number(window.location.pathname.split('/')[2]) || undefined;
  const selectedPromotion = promotions?.data.find(
    (c) => c.id === selectedPromotionId,
  );

  useEffect(() => {
    const initialFilters: Status[] = ['scheduled', 'live'];
    const initialParams = new URLSearchParams(
      initialFilters.map((v) => ['status', v]),
    );

    initialParams.set('sort_by', 'starts_at');
    initialParams.set('sort_dir', 'desc');
    setSearchParams(initialParams);
    // including setSearchParams in dependency array causes an infinite loop
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="my-0 flex h-full flex-col px-6 pb-0">
      <Dialog
        open={showEditPromotionScreen}
        onClose={() => {
          navigate(-1);
        }}
        fullScreen
      >
        <EditPromotion
          campaignId={selectedPromotionId}
          campaignStatus={selectedPromotion?.status as Status | undefined}
          refetch={refetch}
        />
      </Dialog>
      <div className="flex flex-col gap-4">
        {promotionsStatus === 'error' && (
          <div className="flex justify-center p-2">
            <ErrorBanner>
              Something happened, try refreshing the page. If the issue
              persists, contact{' '}
              <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="error">
                {SUPPORT_EMAIL}
              </MuiLink>
            </ErrorBanner>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl">Promotions</h1>
          <Button asChild>
            <Link to={`/promotions/create?${searchParams}`}>
              Schedule Promotion
            </Link>
          </Button>
        </div>
        <PromotionListTableHeader />
        <PromotionListTable
          promotions={promotions?.data ?? []}
          success={promotionsStatus === 'success'}
        />
        <DataTablePagination total={promotions?.meta.totalCount ?? 0} />
      </div>
    </div>
  );
}
